import { Link } from "react-router-dom";
import {
  LOCATION_TYPES,
  RSVP_STATUS,
} from "@circle-react/components/Events/helpers/constants";
import { ShowPageLink } from "@circle-react/components/PostsV3/PostViewListItem/ShowPageLink";
import { useCurrentPostContext } from "@circle-react/contexts";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { postsPath } from "@circle-react/helpers/urlHelpers";
import { DisplayTrixContent } from "@circle-react-uikit/DisplayTrixContent";
import { SeeMoreLess } from "@circle-react-uikit/SeeMoreLess";
import { EventMenu } from "../../ListViewItem/EventMenu";
import { STATUS } from "../../helpers/constants";
import { InPersonLocation } from "../EventDetailPage/InPersonLocation";
import { RsvpActionButton } from "../EventDetailPage/RsvpActionButton";
import { CoverImage, EventInfoChips, RsvpWarningMessages } from "../shared";
import { InvitedByTab } from "../shared/InvitedByTab";
import { canRespondToInvitation, isEventFinished } from "../shared/helpers";
import { ListViewEventTimestamp } from "./ListViewEventTimestamp";

export interface FeaturedEventProps {
  isNextEvent: boolean;
  isThumbnailView?: boolean;
  onDestroy: () => void;
  onRsvp: () => void;
}

export const FeaturedEvent = ({
  onDestroy,
  onRsvp,
  isNextEvent = false,
  isThumbnailView = false,
}: FeaturedEventProps) => {
  const { record: event } = useCurrentPostContext();

  const {
    name,
    status,
    event_setting_attributes: eventData,
    body_trix_content,
    rsvped_event: rsvpedEvent,
    rsvp_status: rsvpStatus,
    community_member: communityMember,
  } = event;

  const {
    hide_location_from_non_attendees: hideLocationFromNonAttendees,
    location_type: locationType,
    in_person_location: inPersonLocation,
  } = eventData || {};

  const isPublished = status === STATUS.PUBLISHED;
  const shouldHideLocation = !rsvpedEvent && hideLocationFromNonAttendees;
  const shouldDisplayRsvpAction =
    isPublished && !isEventFinished(event) && canRespondToInvitation(event);

  const postHref = postsPath({
    spaceSlug: event.space_slug,
    slug: event.slug,
  });
  const isInPersonEventType = locationType === LOCATION_TYPES.IN_PERSON;
  const isAttendeeInvited = rsvpStatus == RSVP_STATUS.INVITED;

  return (
    <div
      className={classNames(
        "bg-primary border-primary mb-12 flex w-full flex-col rounded-2xl border",
        {
          "sm:p-4": !isThumbnailView,
          "sm:p-6": isThumbnailView,
        },
      )}
    >
      {isAttendeeInvited && (
        <InvitedByTab name={communityMember.name} isFeaturedEvent />
      )}
      <CoverImage
        isPinned={!isNextEvent}
        event={event}
        isThumbnailView={isThumbnailView}
      />
      <div
        className={classNames("sm:p-0", {
          "p-4": !isThumbnailView,
          "p-6": isThumbnailView,
        })}
      >
        <div
          className="flex w-full justify-between"
          data-testid="event-main-content"
        >
          <div>
            <ShowPageLink
              post={event}
              className="!text-dark mb-2 !line-clamp-2 break-words text-xl font-semibold"
            >
              {name}
            </ShowPageLink>
            <div>
              <ListViewEventTimestamp event={event} isRenderedOutsideListItem />
            </div>
            <RsvpWarningMessages event={event} />
          </div>
          <div className="ml-2 flex shrink-0 gap-2">
            {shouldDisplayRsvpAction && (
              <div className="hidden md:block">
                <RsvpActionButton event={event} onChange={onRsvp} />
              </div>
            )}
            <EventMenu event={event} onDestroy={onDestroy} />
          </div>
        </div>
        {body_trix_content && (
          <div className="mt-4">
            <Link to={postHref}>
              <SeeMoreLess lines={3} hideToggle>
                <DisplayTrixContent content={body_trix_content} />
              </SeeMoreLess>
            </Link>
          </div>
        )}
        {!shouldHideLocation && isInPersonEventType && inPersonLocation && (
          <InPersonLocation serializedLocation={inPersonLocation} />
        )}
        <EventInfoChips event={event} shouldHideLocation={shouldHideLocation} />
        {shouldDisplayRsvpAction && (
          <div className="mt-4 block md:hidden">
            <RsvpActionButton event={event} onChange={onRsvp} />
          </div>
        )}
      </div>
    </div>
  );
};
